import { Link } from '@tanstack/react-router'
import { BarChart3, Bot, Download, Radio } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Badge,
  BarChart,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  SpinnerIcon,
  StatCard,
} from '@/components/ui'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { buildCsv } from '@/lib/csv'
import { downloadTextFile } from '@/lib/download'
import { translateEnumValue } from '@/lib/formatting'
import { cn } from '@/lib/utils'

import { useAnalyticsOverview } from './queries'
import type { AnalyticsOverview, AnalyticsWindowOption } from './types'

const WINDOW_OPTIONS: readonly AnalyticsWindowOption[] = [7, 30, 90]

/** Formata um numero inteiro no idioma atual (milhares separados). */
function useNumberFormatter(): (value: number) => string {
  const { i18n } = useTranslation()
  return (value: number) => new Intl.NumberFormat(i18n.language).format(value)
}

/** Rótulo curto (DD/MM) de um dia ISO ('2026-08-18'), formatado em UTC para não deslocar o dia. */
function formatDayLabel(isoDay: string, language: string): string {
  const date = new Date(isoDay)
  if (Number.isNaN(date.getTime())) {
    return isoDay
  }
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(date)
}

/** Linha rotulo -> valor de uma quebra (por modo, por status, por modelo...). */
function BreakdownRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-foreground-muted">{label}</span>
      <span className="font-medium text-foreground-primary">{value}</span>
    </div>
  )
}

function SectionCard({
  title,
  children,
}: {
  readonly title: string
  readonly children: ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  )
}

function channelStatusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'Connected':
      return 'success'
    case 'WaitingForPairing':
    case 'Created':
      return 'warning'
    case 'Disconnected':
    case 'Failed':
      return 'danger'
    default:
      return 'neutral'
  }
}

/**
 * Analytics (WP-16): metricas REAIS do tenant, computadas a partir de dados persistidos. Gate = "dados reais, sem
 * metricas inventadas" — cada visual mapeia um numero real; onde nao ha dado, um estado vazio honesto (nunca grafico
 * fingido). Distingue carregando de sem-dados; estados vazios trazem a proxima acao. Vocabulario neutro do produto.
 */
export function AnalyticsPage() {
  const { t } = useTranslation()
  const { tenant } = useCurrentTenant()
  const [windowDays, setWindowDays] = useState<AnalyticsWindowOption>(30)
  const query = useAnalyticsOverview(tenant?.id, windowDays)

  if (tenant === null) {
    return null
  }

  const windowLabels: Readonly<Record<AnalyticsWindowOption, string>> = {
    7: t('analytics.window.last7'),
    30: t('analytics.window.last30'),
    90: t('analytics.window.last90'),
  }

  const daily = query.data?.agentExecutions.daily ?? []

  const exportCsv = (): void => {
    if (daily.length === 0) {
      return
    }
    const headers = [
      t('analytics.export.columns.day'),
      t('analytics.export.columns.executions'),
      t('analytics.export.columns.succeeded'),
      t('analytics.export.columns.failed'),
      t('analytics.export.columns.promptTokens'),
      t('analytics.export.columns.completionTokens'),
    ]
    const rows = daily.map((point) => [
      point.day,
      String(point.total),
      String(point.succeeded),
      String(point.failed),
      String(point.promptTokens),
      String(point.completionTokens),
    ])
    downloadTextFile(buildCsv(headers, rows), 'analytics.csv', 'text/csv')
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground-primary">{t('analytics.title')}</h1>
          <p className="text-sm text-foreground-muted">{t('analytics.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex rounded-lg border border-border-default p-0.5"
            role="group"
            aria-label={t('analytics.window.label')}
          >
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={windowDays === option}
                onClick={() => {
                  setWindowDays(option)
                }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  windowDays === option
                    ? 'bg-action-primary text-foreground-on-action'
                    : 'text-foreground-muted hover:text-foreground-primary',
                )}
              >
                {windowLabels[option]}
              </button>
            ))}
          </div>
          <Button variant="secondary" size="sm" disabled={daily.length === 0} onClick={exportCsv}>
            <Download aria-hidden="true" className="h-4 w-4" />
            {t('analytics.export.button')}
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
          <SpinnerIcon />
          <span className="ml-2 text-sm text-foreground-muted">{t('analytics.loading')}</span>
        </div>
      ) : query.isError ? (
        <Alert variant="danger" title={t('analytics.loadFailed')}>
          {describeApiError(query.error)}
        </Alert>
      ) : query.data !== undefined ? (
        <AnalyticsContent overview={query.data} tenantSlug={tenant.slug} />
      ) : null}
    </div>
  )
}

function AnalyticsContent({
  overview,
  tenantSlug,
}: {
  readonly overview: AnalyticsOverview
  readonly tenantSlug: string
}) {
  const { t, i18n } = useTranslation()
  const formatNumber = useNumberFormatter()

  const { agentExecutions, inbound, knowledge, channelHealth, usage } = overview

  const hasAnyData =
    agentExecutions.total > 0 ||
    inbound.total > 0 ||
    knowledge.totalDocuments > 0 ||
    channelHealth.length > 0 ||
    usage.agents > 0 ||
    usage.channels > 0

  if (!hasAnyData) {
    return (
      <EmptyState
        icon={BarChart3}
        title={t('analytics.empty.title')}
        description={t('analytics.empty.description')}
      />
    )
  }

  const unavailable = t('analytics.unavailable')
  const formatMs = (value: number | null): string =>
    value === null
      ? unavailable
      : t('analytics.units.ms', { value: formatNumber(Math.round(value)) })
  const successRate =
    agentExecutions.total > 0
      ? t('analytics.units.percent', {
          value: formatNumber(
            Math.round((agentExecutions.succeeded / agentExecutions.total) * 100),
          ),
        })
      : unavailable

  const agentModeLabels: Readonly<Record<string, string>> = {
    ObserveOnly: t('analytics.agent.modes.ObserveOnly'),
    Suggest: t('analytics.agent.modes.Suggest'),
    AutoReply: t('analytics.agent.modes.AutoReply'),
  }
  const inboundStatusLabels: Readonly<Record<string, string>> = {
    Pending: t('analytics.inbound.statuses.Pending'),
    Processed: t('analytics.inbound.statuses.Processed'),
    Skipped: t('analytics.inbound.statuses.Skipped'),
    Failed: t('analytics.inbound.statuses.Failed'),
  }
  const channelStatusLabels: Readonly<Record<string, string>> = {
    Created: t('analytics.channels.statuses.Created'),
    WaitingForPairing: t('analytics.channels.statuses.WaitingForPairing'),
    Connected: t('analytics.channels.statuses.Connected'),
    Disconnected: t('analytics.channels.statuses.Disconnected'),
    Failed: t('analytics.channels.statuses.Failed'),
  }
  const channelTypeLabels: Readonly<Record<string, string>> = {
    WhatsApp: t('analytics.channels.types.WhatsApp'),
    WebChat: t('analytics.channels.types.WebChat'),
    Email: t('analytics.channels.types.Email'),
    Instagram: t('analytics.channels.types.Instagram'),
    Telegram: t('analytics.channels.types.Telegram'),
    Teams: t('analytics.channels.types.Teams'),
    Api: t('analytics.channels.types.Api'),
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Atividade do agente */}
      <SectionCard title={t('analytics.agent.section')}>
        {agentExecutions.total === 0 ? (
          <EmptyState
            icon={Bot}
            title={t('analytics.agent.emptyTitle')}
            description={t('analytics.agent.emptyDescription')}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                label={t('analytics.agent.total')}
                value={formatNumber(agentExecutions.total)}
              />
              <StatCard label={t('analytics.agent.successRate')} value={successRate} />
              <StatCard
                label={t('analytics.agent.failed')}
                value={formatNumber(agentExecutions.failed)}
              />
              <StatCard
                label={t('analytics.agent.suggestions')}
                value={formatNumber(agentExecutions.suggestionsToHuman)}
                hint={t('analytics.agent.suggestionsHint')}
              />
              <StatCard
                label={t('analytics.agent.autoReplies')}
                value={formatNumber(agentExecutions.autoReplies)}
                hint={t('analytics.agent.autoRepliesHint')}
              />
              <StatCard
                label={t('analytics.agent.avgLatency')}
                value={formatMs(agentExecutions.averageDurationMs)}
              />
              <StatCard
                label={t('analytics.agent.p95Latency')}
                value={formatMs(agentExecutions.p95DurationMs)}
              />
            </div>

            {agentExecutions.daily.length > 0 ? (
              <div>
                <p className="mb-2 text-sm font-medium text-foreground-primary">
                  {t('analytics.agent.daily')}
                </p>
                <BarChart
                  ariaLabel={t('analytics.agent.dailyAria')}
                  formatValue={formatNumber}
                  series={[
                    {
                      key: 'succeeded',
                      label: t('analytics.agent.succeeded'),
                      colorClassName: 'bg-status-success',
                    },
                    {
                      key: 'failed',
                      label: t('analytics.agent.failed'),
                      colorClassName: 'bg-status-danger',
                    },
                  ]}
                  points={agentExecutions.daily.map((day) => ({
                    key: day.day,
                    label: formatDayLabel(day.day, i18n.language),
                    values: { succeeded: day.succeeded, failed: day.failed },
                  }))}
                />
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-sm font-medium text-foreground-primary">
                  {t('analytics.agent.byMode')}
                </p>
                <div className="divide-y divide-border-default">
                  {agentExecutions.byMode.map((entry) => (
                    <BreakdownRow
                      key={entry.mode}
                      label={translateEnumValue(agentModeLabels, entry.mode)}
                      value={formatNumber(entry.count)}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-sm font-medium text-foreground-primary">
                  {t('analytics.agent.byModel')}
                </p>
                <div className="divide-y divide-border-default">
                  {agentExecutions.byModel.map((entry) => (
                    <BreakdownRow
                      key={`${entry.provider}/${entry.model}`}
                      label={entry.model}
                      value={formatNumber(entry.count)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </SectionCard>

      {/* Transferencias para humano: sem funil real -> nota honesta + o proxy real (sugestoes ao operador). */}
      <Alert variant="info" title={t('analytics.handoffs.title')}>
        {t('analytics.handoffs.description')}
      </Alert>

      {/* Mensagens recebidas */}
      <SectionCard title={t('analytics.inbound.section')}>
        {inbound.total === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={t('analytics.inbound.emptyTitle')}
            description={t('analytics.inbound.emptyDescription')}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <StatCard label={t('analytics.inbound.total')} value={formatNumber(inbound.total)} />
              <StatCard
                label={t('analytics.inbound.avgProcessing')}
                value={formatMs(inbound.averageProcessingMs)}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-sm font-medium text-foreground-primary">
                  {t('analytics.inbound.byStatus')}
                </p>
                <div className="divide-y divide-border-default">
                  {inbound.byStatus.map((entry) => (
                    <BreakdownRow
                      key={entry.status}
                      label={translateEnumValue(inboundStatusLabels, entry.status)}
                      value={formatNumber(entry.count)}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-sm font-medium text-foreground-primary">
                  {t('analytics.inbound.failures')}
                </p>
                {inbound.failuresByErrorCode.length === 0 ? (
                  <p className="py-1.5 text-sm text-foreground-muted">
                    {t('analytics.inbound.noFailures')}
                  </p>
                ) : (
                  <div className="divide-y divide-border-default">
                    {inbound.failuresByErrorCode.map((entry) => (
                      <BreakdownRow
                        key={entry.errorCode}
                        label={entry.errorCode}
                        value={formatNumber(entry.count)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </SectionCard>

      {/* Conhecimento */}
      <SectionCard title={t('analytics.knowledge.section')}>
        {knowledge.totalDocuments === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={t('analytics.knowledge.emptyTitle')}
            description={t('analytics.knowledge.emptyDescription')}
            action={
              <Link
                to="/app/$tenantSlug/knowledge"
                params={{ tenantSlug }}
                className={buttonVariants({ variant: 'primary', size: 'sm' })}
              >
                {t('analytics.knowledge.addAction')}
              </Link>
            }
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard
              label={t('analytics.knowledge.documents')}
              value={formatNumber(knowledge.totalDocuments)}
            />
            <StatCard
              label={t('analytics.knowledge.ready')}
              value={formatNumber(knowledge.ready)}
            />
            <StatCard
              label={t('analytics.knowledge.processing')}
              value={formatNumber(knowledge.processing)}
            />
            <StatCard
              label={t('analytics.knowledge.failed')}
              value={formatNumber(knowledge.failed)}
            />
            <StatCard
              label={t('analytics.knowledge.chunks')}
              value={formatNumber(knowledge.totalChunks)}
            />
          </div>
        )}
      </SectionCard>

      {/* Canais */}
      <SectionCard title={t('analytics.channels.section')}>
        {channelHealth.length === 0 ? (
          <EmptyState
            icon={Radio}
            title={t('analytics.channels.emptyTitle')}
            description={t('analytics.channels.emptyDescription')}
            action={
              <Link
                to="/app/$tenantSlug/channels"
                params={{ tenantSlug }}
                className={buttonVariants({ variant: 'primary', size: 'sm' })}
              >
                {t('analytics.channels.connectAction')}
              </Link>
            }
          />
        ) : (
          <div className="flex flex-col divide-y divide-border-default">
            {channelHealth.map((channel) => (
              <div
                key={`${channel.name}/${channel.channelType}`}
                className="flex items-center justify-between gap-4 py-2"
              >
                <div>
                  <p className="text-sm font-medium text-foreground-primary">{channel.name}</p>
                  <p className="text-xs text-foreground-muted">
                    {translateEnumValue(channelTypeLabels, channel.channelType)}
                  </p>
                </div>
                <Badge variant={channelStatusVariant(channel.status)}>
                  {translateEnumValue(channelStatusLabels, channel.status)}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Uso */}
      <SectionCard title={t('analytics.usage.section')}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label={t('analytics.usage.agents')} value={formatNumber(usage.agents)} />
          <StatCard label={t('analytics.usage.channels')} value={formatNumber(usage.channels)} />
          <StatCard label={t('analytics.usage.users')} value={formatNumber(usage.users)} />
          <StatCard label={t('analytics.usage.documents')} value={formatNumber(usage.documents)} />
          <StatCard
            label={t('analytics.usage.aiExecutions')}
            value={formatNumber(usage.aiExecutions)}
          />
          <StatCard
            label={t('analytics.usage.promptTokens')}
            value={formatNumber(usage.promptTokens)}
          />
          <StatCard
            label={t('analytics.usage.completionTokens')}
            value={formatNumber(usage.completionTokens)}
          />
          <StatCard
            label={t('analytics.usage.totalTokens')}
            value={formatNumber(usage.totalTokens)}
          />
        </div>
      </SectionCard>
    </div>
  )
}
